// Briefing/export card with status badge pipeline + version + scope chip + action row.

const STATUS_TONE = {
  draft: 'slate',
  drafting: 'slate',
  pending_review: 'amber',
  pendingreview: 'amber',
  pending: 'amber',
  approved_for_export: 'sage',
  approved: 'sage',
  exported: 'blue',
  downloaded: 'blue',
  needs_rework: 'brick',
};

const STATUS_LABEL = {
  draft: 'Draft',
  drafting: 'Drafting',
  pending: 'Pending review',
  pending_review: 'Pending review',
  pendingreview: 'Pending review',
  pendingReview: 'Pending review',
  approved_for_export: 'Approved for export',
  approved: 'Approved',
  exported: 'Exported',
  downloaded: 'Downloaded',
  needs_rework: 'Needs rework',
};

function prettyBriefStatus(raw) {
  if (!raw) return 'Draft';
  const key = String(raw);
  if (STATUS_LABEL[key]) return STATUS_LABEL[key];
  const lower = key.toLowerCase();
  if (STATUS_LABEL[lower]) return STATUS_LABEL[lower];
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());
}

// Statuses that sit at or past the human approval gate. Anything earlier
// (draft / pending review / needs rework) must not expose live export actions:
// a named human approves before anything leaves the system (AC-PROD-2.1).
const EXPORT_UNLOCKED_STATUSES = new Set([
  'approved',
  'approved_for_export',
  'generated',
  'exported',
  'downloaded',
]);

const EXPORT_GATE_EXPLANATION =
  'Export unlocks after a named human approves this product for export.';

// Deck-type products (e.g. the OWHA Director deck, TPL-DECK-OWHA-DIRECTOR) list
// `pptx` among their export formats. Only surface the PPTX action for those
// products so the factory card matches the product detail page, where PPTX is
// already reachable for decks.
function supportsExportFormat(briefing, format) {
  const formats = Array.isArray(briefing?.exportFormats)
    ? briefing.exportFormats
    : Array.isArray(briefing?.outputFormats)
      ? briefing.outputFormats
      : [];
  return formats.some((value) => String(value || '').toLowerCase() === format);
}

export function BriefingExportCard({
  briefing,
  onExportHtml,
  onExportPdf,
  onExportPptx,
  onApprove,
  onOpen,
  busy,
}) {
  const tone = STATUS_TONE[String(briefing.status || 'draft').toLowerCase()] || 'slate';
  const exportUnlocked = EXPORT_UNLOCKED_STATUSES.has(
    String(briefing.status || 'draft').toLowerCase(),
  );
  const showPptxExport = Boolean(onExportPptx) && supportsExportFormat(briefing, 'pptx');
  return (
    <article className="briefing-card">
      <header className="briefing-head">
        <div className="stack tight">
          <div className="eyebrow">
            {(briefing.scopeType || 'briefing')
              .toString()
              .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
              .replace(/[_-]+/g, ' ')}{' '}
            · {briefing.scopeId || briefing.id}
          </div>
          <h4 className="briefing-title">{briefing.title}</h4>
        </div>
        <span className={`chip ${tone} sm`}>{prettyBriefStatus(briefing.status)}</span>
      </header>
      <p className="muted small">
        {briefing.format?.toUpperCase() || 'HTML'} · v{briefing.version || 1}
        {briefing.generatedAt
          ? ` · generated ${new Date(briefing.generatedAt).toLocaleString()}`
          : ''}
      </p>
      {briefing.citationCount ? (
        <span className="chip outline sm">{briefing.citationCount} citations</span>
      ) : null}
      <footer className="briefing-actions">
        <button className="btn ghost sm" onClick={() => onOpen && onOpen(briefing)}>
          Open
        </button>
        <button
          className="btn outline sm"
          onClick={() => onExportHtml && onExportHtml(briefing)}
          disabled={busy || !exportUnlocked}
          title={exportUnlocked ? undefined : EXPORT_GATE_EXPLANATION}
        >
          Export HTML
        </button>
        {/* No handler means the environment cannot produce PDFs (the factory
            passes undefined then) — hide the action rather than render a dead
            button, matching the detail page's hidden-format behavior. */}
        {onExportPdf ? (
          <button
            className="btn outline sm"
            onClick={() => onExportPdf(briefing)}
            disabled={busy || !exportUnlocked}
            title={exportUnlocked ? undefined : EXPORT_GATE_EXPLANATION}
          >
            Export PDF
          </button>
        ) : null}
        {showPptxExport ? (
          <button
            className="btn outline sm"
            onClick={() => onExportPptx && onExportPptx(briefing)}
            disabled={busy || !exportUnlocked}
            title={exportUnlocked ? undefined : EXPORT_GATE_EXPLANATION}
          >
            Export PPTX
          </button>
        ) : null}
        <button
          className="btn success sm tactile-press"
          onClick={() => onApprove && onApprove(briefing)}
          disabled={
            // G7-APP-002: anything at or past the approval gate has a settled
            // approval — exported products must not re-offer Approve.
            busy || EXPORT_UNLOCKED_STATUSES.has(String(briefing.status || '').toLowerCase())
          }
        >
          Approve
        </button>
      </footer>
    </article>
  );
}
